import React, { useState } from 'react';
import { FiCalendar, FiGlobe, FiSearch, FiTag, FiUser } from 'react-icons/fi';

import ConditionCombobox from './ConditionCombobox';

const AVAILABILITY = [
  'Any time',
  'This week',
  'Next 2 weeks',
  'Evenings',
  'Weekends',
];

// Therapy is done in words, so the language someone thinks in is a harder
// constraint than the hour they are free. Ordered by number of speakers rather
// than alphabetically — an alphabetical list buries Hindi under Gujarati.
const LANGUAGES = [
  'Any language',
  'English',
  'Hindi',
  'Bengali',
  'Marathi',
  'Telugu',
  'Tamil',
  'Gujarati',
  'Urdu',
  'Kannada',
  'Malayalam',
  'Odia',
  'Punjabi',
  'Assamese',
];

const FOR_WHO = ['Myself', 'Me and my partner', 'My child', 'A family member', 'My team'];

/**
 * The floating hero search pill: condition (free text with suggestions),
 * availability, and who the session is for.
 *
 * Controlled by local state and handed up via `onSearch` — the parent decides
 * whether that means a route change or an API call.
 */
export default function SearchBar({ categories = [], onSearch }) {
  const [condition, setCondition] = useState('');
  const [availability, setAvailability] = useState(AVAILABILITY[0]);
  const [language, setLanguage] = useState(LANGUAGES[0]);
  const [forWho, setForWho] = useState(FOR_WHO[0]);

  const handleSubmit = (event) => {
    event.preventDefault();
    onSearch?.({ condition, availability, language, forWho });
  };

  return (
    <form className="mm-search" onSubmit={handleSubmit} role="search" id="search">
      <div className="mm-search__field">
        <span className="mm-search__icon" aria-hidden="true">
          <FiTag size={17} />
        </span>
        <div className="mm-search__body">
          {/* Free text with suggestions. See ConditionCombobox for why this
              is not a native <datalist>. */}
          <ConditionCombobox
            value={condition}
            onChange={setCondition}
            options={categories}
          />
        </div>
      </div>

      <span className="mm-search__divider" aria-hidden="true" />

      <div className="mm-search__field">
        <span className="mm-search__icon" aria-hidden="true">
          <FiCalendar size={17} />
        </span>
        <div className="mm-search__body">
          <label className="mm-search__label" htmlFor="mm-availability">
            Availability
          </label>
          <select
            id="mm-availability"
            className="mm-search__control"
            value={availability}
            onChange={(e) => setAvailability(e.target.value)}
          >
            {AVAILABILITY.map((option) => (
              <option key={option}>{option}</option>
            ))}
          </select>
        </div>
      </div>

      <span className="mm-search__divider" aria-hidden="true" />

      <div className="mm-search__field">
        <span className="mm-search__icon" aria-hidden="true">
          <FiGlobe size={17} />
        </span>
        <div className="mm-search__body">
          <label className="mm-search__label" htmlFor="mm-language">
            Language
          </label>
          <select
            id="mm-language"
            className="mm-search__control"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            {LANGUAGES.map((option) => (
              <option key={option}>{option}</option>
            ))}
          </select>
        </div>
      </div>

      <span className="mm-search__divider" aria-hidden="true" />

      <div className="mm-search__field">
        <span className="mm-search__icon" aria-hidden="true">
          <FiUser size={17} />
        </span>
        <div className="mm-search__body">
          <label className="mm-search__label" htmlFor="mm-for-who">
            For Who
          </label>
          <select
            id="mm-for-who"
            className="mm-search__control"
            value={forWho}
            onChange={(e) => setForWho(e.target.value)}
          >
            {FOR_WHO.map((option) => (
              <option key={option}>{option}</option>
            ))}
          </select>
        </div>
      </div>

      <button
        type="submit"
        className="mm-icon-btn mm-icon-btn--primary mm-search__submit"
        aria-label="Search therapists"
      >
        <FiSearch size={20} aria-hidden="true" />
        <span className="mm-search__submit-text">Search therapists</span>
      </button>
    </form>
  );
}
